import { EmptyState } from '@pointfive/ui';
import { api } from '@/client/api';
import { useProcedure } from '@/client/useProcedure';
import { ago, Section, SettingCard, SettingRow, ToneChip, until } from './kit';

/** Settings → Lockout Settings.
 *
 *  This section deliberately offers **no way to lift a lockout**. That is the point of one: a control a trader can
 *  switch off at the moment it starts costing them is not a control, it is a suggestion. What it does instead is make
 *  the machinery legible — what is in force, why, and when it ends.
 *
 *  The same reasoning governs the queue below it. Strengthening a rule applies at once; weakening one waits out a
 *  refractory delay of two to twenty-four hours (ADR-014, §11.2), visibly and cancellably. The moment a trader most
 *  wants to loosen a limit is the moment the limit is doing its work, and the delay is the whole control. Showing the
 *  queue is how the trader knows the delay is real rather than something the system claims.
 */
export function LockoutSection() {
  const now = Date.now();
  const { state, reload } = useProcedure(() => api.me.lockouts.get(), []);

  return (
    <Section state={state} reload={reload}>
      {(data) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <SettingCard
            title="Active lockouts"
            sub="A lockout cannot be lifted from this screen. One you could switch off when it started costing you would not be a control."
          >
            {data.active.length === 0 ? (
              <EmptyState
                icon="ph-lock-simple-open"
                title="No lockouts in force"
                guidance="Breaching a risk rule opens one. It clears on its own clock."
              />
            ) : (
              data.active.map((l) => (
                <SettingRow
                  key={l.id}
                  label={l.reason}
                  hint={
                    <span title={`Started ${l.startedAt}`}>
                      Started {ago(l.startedAt, now)}
                      {l.expiresAt === null
                        ? ' · until you close the session'
                        : ` · lifts ${until(l.expiresAt, now)}`}
                    </span>
                  }
                >
                  {/* Soft and hard are different promises: a soft lockout warns and can be overridden with a
                      recorded reason, a hard one does not open until its clock runs out. */}
                  <ToneChip tone={l.severity === 'hard' ? 'neg' : 'warn'}>
                    {l.severity === 'hard' ? 'Hard' : 'Soft'}
                  </ToneChip>
                </SettingRow>
              ))
            )}
          </SettingCard>

          <SettingCard
            title="Changes waiting"
            sub={`Loosening a risk control waits ${data.refractoryHours.min}–${data.refractoryHours.max} hours before it takes effect. Tightening one applies immediately — nobody needs protecting from becoming stricter.`}
          >
            {data.pending.length === 0 ? (
              <EmptyState
                icon="ph-hourglass-medium"
                title="Nothing waiting"
                guidance="A weakened rule would appear here, with the time it takes effect, until then."
              />
            ) : (
              data.pending.map((p) => (
                <SettingRow
                  key={p.id}
                  label={p.settingKey}
                  hint={
                    <span title={`Requested ${p.requestedAt} · releases ${p.releasesAt}`}>
                      {p.currentValue === null ? 'unset' : p.currentValue} → {p.requestedValue} · requested{' '}
                      {ago(p.requestedAt, now)} · {p.scope}
                    </span>
                  }
                >
                  <ToneChip tone="warn">Takes effect {until(p.releasesAt, now)}</ToneChip>
                </SettingRow>
              ))
            )}
          </SettingCard>
        </div>
      )}
    </Section>
  );
}
